// src/pages/Personal.jsx
import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  Fade,
  Grow,
  Zoom,
  alpha
} from '@mui/material';
import {
  AccountBalance,
  AttachMoney,
  CreditCard,
  Payments,
  Phone,
  Email,
  LocationOn,
  CheckCircle,
  MobileFriendly,
  Security,
  SupportAgent,
  ArrowForward,
  Diamond,
  TrendingUp,
  Savings
} from '@mui/icons-material';

// Import images from public folder
const personalHero = '/images/herosection.png';
const silverAccount = '/images/daimondbank_silvercard.png';
const goldAccount = '/images/diamondbank_diamondcard.png';
const platinumAccount = '/images/diamondcard_platnumcard.png';
const diamondAccount = '/images/diamondcards.png';

// DiamondBank Zimbabwe color palette
const colors = {
  primary: '#470a91',
  primaryLight: '#5e1bb5',
  primaryDark: '#35076d',
  secondary: '#d2d8e1',
  secondaryDark: '#b0b8c4',
  accent: '#000000',
  lightBg: '#f8f9fa',
  textDark: '#1a1a1a',
  textLight: '#ffffff',
  gray: '#6b7280'
};

const Personal = () => {
  const accountTypes = [
    {
      icon: <AttachMoney sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Silver Account",
      description: "Perfect for everyday banking with essential features",
      features: [
        "Low ZWL 1,000 activation fee",
        "8 free ATM withdrawals",
        "Full online banking access",
        "ZWL 2,500 monthly maintenance"
      ],
      action: "View Details",
      image: silverAccount,
      gradient: `linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)`,
      textColor: colors.textDark
    },
    {
      icon: <CreditCard sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Gold Account",
      description: "Enhanced features for more frequent banking needs",
      features: [
        "ZWL 8,000 monthly fee",
        "12 free ATM withdrawals",
        "40 free POS transactions",
        "Priority customer service"
      ],
      action: "View Details",
      image: goldAccount,
      gradient: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
      textColor: colors.white
    },
    {
      icon: <Payments sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Platinum Account",
      description: "Premium services for high-value banking",
      features: [
        "ZWL 15,000 monthly fee",
        "20 free ATM withdrawals",
        "Unlimited POS transactions",
        "Dedicated relationship manager"
      ],
      action: "View Details",
      image: platinumAccount,
      gradient: `linear-gradient(135deg, #E0E0E0 0%, #B8B8B8 100%)`,
      textColor: colors.textDark
    },
    {
      icon: <Diamond sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Diamond Account",
      description: "Exclusive benefits for our most valued customers",
      features: [
        "ZWL 35,000 monthly fee",
        "Unlimited ATM withdrawals",
        "Global concierge services",
        "Premium travel benefits"
      ],
      action: "View Details",
      image: diamondAccount,
      gradient: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
      textColor: colors.white
    }
  ];

  const features = [
    {
      icon: <MobileFriendly sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Mobile Banking",
      description: "Bank anytime, anywhere with our award-winning secure app"
    },
    {
      icon: <Security sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Advanced Security",
      description: "Multi-layered protection with biometric authentication"
    },
    {
      icon: <SupportAgent sx={{ fontSize: 40, color: colors.primary }} />,
      title: "24/7 Support",
      description: "Dedicated customer service available around the clock"
    },
    {
      icon: <Savings sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Smart Savings",
      description: "Automated savings tools to help you reach your goals"
    }
  ];

  const benefits = [
    "No hidden fees or charges",
    "Instant money transfers",
    "Customizable account alerts",
    "Free monthly statements",
    "Overdraft protection available",
    "Mobile check deposit"
  ];

  return (
    <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: '80vh',
          minHeight: '600px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `linear-gradient(135deg, ${colors.primary}dd, ${colors.primaryDark}aa), url(${personalHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100px',
            background: `linear-gradient(to top, ${colors.lightBg}, transparent)`,
            zIndex: 1
          }
        }}
      >
        {/* Animated background pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.secondary} 2%, transparent 2.5%)`,
          backgroundSize: '30px 30px',
          animation: 'pulse 20s linear infinite'
        }} />

        <Container>
          <Fade in timeout={800}>
            <Box>
              <Diamond sx={{ fontSize: 60, color: colors.secondary, mb: 2, animation: 'spin 10s linear infinite' }} />
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                Personal <Box component="span" sx={{ color: colors.secondary }}>Banking</Box> Solutions
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  maxWidth: '800px',
                  mx: 'auto',
                  mb: 4,
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1.1rem', md: '1.5rem' },
                  fontWeight: 300,
                  opacity: 0.95
                }}
              >
                Accounts designed to fit your lifestyle and help you achieve your financial goals in Zimbabwe
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="/apply"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    background: `linear-gradient(135deg, ${colors.secondary}, ${colors.white})`,
                    color: colors.primary,
                    boxShadow: `0 4px 20px ${colors.primary}80`,
                    '&:hover': {
                      boxShadow: `0 6px 25px ${colors.primary}b3`,
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    fontWeight: 600
                  }}
                >
                  Open an Account
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="/compare"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderWidth: '2px',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    fontWeight: 600
                  }}
                >
                  Compare Accounts
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container sx={{ py: 10 }}>
        {/* Current Account Section */}
        <Grid container spacing={6} sx={{ mb: 10 }}>
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000}>
              <Box>
                <Typography
                  variant="h2"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    color: colors.primary,
                    mb: 3,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -10,
                      left: 0,
                      width: '80px',
                      height: '4px',
                      backgroundColor: colors.primary,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Personal Current Account
                </Typography>
                <Typography variant="h5" sx={{ color: colors.gray, fontWeight: 400, mb: 2 }}>
                  Your everyday banking companion in Zimbabwe
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: colors.gray }}>
                  Manage your finances effortlessly with our feature-packed current account. Pay bills, send money,
                  and track your spending with our intuitive digital banking platforms.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  {[
                    "No minimum balance requirement",
                    "Free online and mobile banking",
                    "Instant money transfers within Zimbabwe",
                    "Customizable alerts and notifications"
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <CheckCircle sx={{ color: colors.primary, mr: 2 }} />
                      <Typography variant="body1" sx={{ color: colors.textDark }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    href="/apply"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      borderRadius: '8px',
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                      boxShadow: `0 4px 12px ${alpha(colors.primary, 0.4)}`,
                      '&:hover': {
                        boxShadow: `0 6px 16px ${alpha(colors.primary, 0.5)}`,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease',
                      fontWeight: 600
                    }}
                  >
                    Open Your Account
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    href="/features"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      borderRadius: '8px',
                      borderWidth: '2px',
                      borderColor: colors.primary,
                      color: colors.primary,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: `${alpha(colors.primary, 0.05)}`,
                        borderWidth: '2px',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    View Features
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grow in timeout={1000}>
              <Paper
                elevation={6}
                sx={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  height: '100%',
                  minHeight: 400,
                  position: 'relative',
                  transform: 'perspective(1000px) rotateY(-5deg)',
                  '&:hover': {
                    transform: 'perspective(1000px) rotateY(-5deg) translateY(-10px)',
                    boxShadow: `0 30px 60px -10px ${alpha(colors.primary, 0.3)}`
                  },
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(to bottom right, ${alpha(colors.primary, 0.1)}, ${alpha(colors.primary, 0.2)})`,
                    zIndex: 1
                  }
                }}
              >
                <Box
                  component="img"
                  src={personalHero}
                  alt="Personal Banking"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: 3,
                  background: `linear-gradient(to top, ${colors.primary}, transparent)`,
                  color: 'white',
                  zIndex: 2,
                  textAlign: 'left'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Digital Banking</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Experience our next-gen banking platform in Zimbabwe</Typography>
                </Box>
              </Paper>
            </Grow>
          </Grid>
        </Grid>

        {/* Account Types Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.primary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              CHOOSE YOUR ACCOUNT
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                  borderRadius: '2px'
                }
              }}
            >
              Account Options
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: colors.gray, fontWeight: 300 }}>
              Find the perfect account for your financial needs in Zimbabwe
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {accountTypes.map((account, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Zoom in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      background: 'white',
                      boxShadow: `0 10px 30px ${alpha(colors.primary, 0.08)}`,
                      border: `1px solid ${alpha(colors.primary, 0.1)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 15px 35px ${alpha(colors.primary, 0.15)}`,
                        borderColor: `${alpha(colors.primary, 0.2)}`,
                        transform: 'translateY(-10px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 150,
                        background: account.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          height: '20px',
                          background: 'linear-gradient(to bottom, transparent, white)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.3)',
                          backdropFilter: 'blur(5px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                        }}
                      >
                        {account.icon}
                      </Box>
                    </Box>
                    <Box sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h5" gutterBottom sx={{ color: colors.primary, fontWeight: 600, textAlign: 'center' }}>
                        {account.title}
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ color: colors.gray, textAlign: 'center', mb: 2 }}>
                        {account.description}
                      </Typography>
                      <List dense sx={{ mb: 2, flexGrow: 1 }}>
                        {account.features.map((feature, i) => (
                          <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <CheckCircle sx={{ color: colors.primary, fontSize: 14 }} />
                            </ListItemIcon>
                            <Typography variant="body2" sx={{ color: colors.textDark }}>{feature}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                    <Box sx={{ p: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        href={`/accounts/${account.title.toLowerCase().replace(/\s+/g, '-')}`}
                        sx={{
                          borderRadius: '50px',
                          py: 1.5,
                          background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                          fontWeight: 600,
                          '&:hover': {
                            background: `linear-gradient(135deg, ${colors.primaryLight}, ${colors.primary})`,
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        {account.action}
                      </Button>
                    </Box>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Benefits Section */}
        <Box sx={{ mb: 10, p: 4, borderRadius: '16px', background: `linear-gradient(135deg, ${alpha(colors.primary, 0.05)} 0%, ${alpha(colors.secondary, 0.05)} 100%)` }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ color: colors.primary, fontWeight: 700, mb: 3 }}>
                All Accounts Include
              </Typography>
              <Grid container spacing={2}>
                {benefits.map((benefit, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: colors.primary, mr: 1, fontSize: 18 }} />
                      <Typography variant="body2">{benefit}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 80, color: colors.primary, mb: 2 }} />
                <Typography variant="h5" sx={{ color: colors.primary, fontWeight: 600 }}>
                  Grow Your Wealth
                </Typography>
                <Typography variant="body2" sx={{ color: colors.gray }}>
                  Access exclusive investment opportunities and wealth management services in Zimbabwe
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Features Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.primary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              WHY CHOOSE US
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                  borderRadius: '2px'
                }
              }}
            >
              Banking Benefits
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: colors.gray, fontWeight: 300 }}>
              Experience banking that works for you
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in timeout={800} style={{ transitionDelay: `${index * 150}ms` }}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      borderRadius: '16px',
                      background: 'white',
                      boxShadow: `0 10px 30px ${alpha(colors.primary, 0.08)}`,
                      border: `1px solid ${alpha(colors.primary, 0.1)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 15px 35px ${alpha(colors.primary, 0.15)}`,
                        borderColor: `${alpha(colors.primary, 0.2)}`,
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: `${alpha(colors.primary, 0.1)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.gray }}>
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
            color: 'white',
            borderRadius: '16px',
            p: 8,
            mb: 6,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.secondary} 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
              opacity: 0.1
            }
          }}
        >
          <Fade in timeout={800}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
                Ready to Get Started?
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
                Open your account today in just 10 minutes
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="/apply"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    backgroundColor: colors.secondary,
                    color: colors.primary,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: colors.white,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Apply Now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="/contact"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderWidth: '2px',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  Contact Us
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>

        {/* Contact Section */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: '16px',
            background: 'white',
            boxShadow: `0 10px 30px ${alpha(colors.primary, 0.08)}`,
            border: `1px solid ${alpha(colors.primary, 0.1)}`
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" sx={{ color: colors.primary, fontWeight: 600, mb: 2 }}>
                Need Help Choosing an Account?
              </Typography>
              <Typography variant="body2" sx={{ color: colors.gray }}>
                Our banking specialists are here to help you find the perfect account for your needs in Zimbabwe
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                variant="contained"
                href="/contact"
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                  px: 4,
                  py: 1.5,
                  borderRadius: '50px',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${colors.primaryLight}, ${colors.primary})`,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Speak to an Expert
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default Personal;